import { useState, useMemo } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { formatEventDate } from "../../../../../utils/dateFormatter";
import { EVENT_TYPES } from "../../../../../hooks/event-hooks/useEvent";
import { H6, Body2, Body3 } from "../../../../../themes/typography";

export default function ScheduleOverview({
    eventType,
    schedule = [],
}) {

    // console.log("Event Type in ScheduleOverview:", eventType);
    // console.log("Schedule Data in ScheduleOverview:", schedule);

    const [page, setPage] = useState(1);
    const itemsPerPage = 3;

    // Total pages only relevant if more than 3 items (for multiDay)
    const totalPages =
        eventType === EVENT_TYPES.multiDay && schedule.length > itemsPerPage
            ? Math.ceil(schedule.length / itemsPerPage)
            : 1;


    // Paginated subset (only for multiDay events)
    const paginatedSchedule = useMemo(() => {
        if (eventType !== EVENT_TYPES.multiDay) return schedule;
        if (schedule.length <= itemsPerPage) return schedule;

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return schedule.slice(startIndex, endIndex);
    }, [schedule, page, eventType]);

    const handlePrev = () => setPage((p) => Math.max(1, p - 1));
    const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));


    return (
        <div className="event-schedule-overview-container">
            {/* Header */}
            <div className="event-schedule-header">
                <div className="event-schedule-title">
                    <H6 fontWeight={600}>Event Schedule</H6>
                </div>
            </div>

            {/* Schedule List */}
            <div className="event-schedule-list">

                {/* 🟩 Continuous Event */}
                {eventType === EVENT_TYPES.continuous && (
                    <div className="event-schedule-accordion event-schedule-single">
                        <div className="event-schedule-summary">
                            <div className="event-schedule-date">
                                <Body2 color="var(--secondary-color)">
                                    {formatEventDate(schedule[0]?.date)} -{" "}
                                    {formatEventDate(schedule[schedule.length - 1]?.date)}
                                </Body2>
                            </div>
                            <div className="event-schedule-time">
                                <Body3>
                                    🕒 {schedule[0]?.from_time} - {schedule[schedule.length - 1]?.to_time}
                                </Body3>
                            </div>
                        </div>
                    </div>
                )}

                {/* 🟦 Single Day Event (no slots) */}
                {eventType === EVENT_TYPES.singleDay &&
                    (schedule[0]?.slots?.length === 0 || !schedule[0]?.slots) && (
                        <div className="event-schedule-accordion event-schedule-single">
                            <div className="event-schedule-summary">
                                <div className="event-schedule-date">
                                    <Body2 color="var(--secondary-color)">
                                        {formatEventDate(schedule[0]?.date)}
                                    </Body2>
                                </div>
                                <div className="event-schedule-time">
                                    <Body3>
                                        🕒 {schedule[0]?.from_time} - {schedule[0]?.to_time}
                                    </Body3>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* 🟨 Single Day Event (with slots) */}
                {eventType === EVENT_TYPES.singleDay &&
                    schedule[0]?.slots?.length > 0 && (
                        <Accordion className="event-schedule-accordion">
                            <AccordionSummary
                                expandIcon={<ExpandMore color="white" />}
                            >
                                <div className="event-schedule-summary">
                                    <div className="event-schedule-date">
                                        <Body2 color="var(--secondary-color)">
                                            {formatEventDate(schedule[0]?.date)}
                                        </Body2>
                                    </div>
                                    <div className="event-schedule-time">
                                        <Body3>
                                            🕒 {schedule[0]?.from_time} - {schedule[0]?.to_time}
                                        </Body3>
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    paddingTop: "0",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.5rem",
                                }}
                            >
                                {schedule[0]?.slots?.map((slot, index) => (
                                    <div key={index} className="event-schedule-slot">
                                        <Body3>
                                            Slot {index + 1}: {slot.from_time} - {slot.to_time}
                                        </Body3>
                                    </div>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    )}

                {/* 🟧 Multi-Day Event (NEW) */}
                {eventType === EVENT_TYPES.multiDay && paginatedSchedule?.length > 0 && (
                    <>
                        {paginatedSchedule.map((day, index) =>
                            day.slots?.length > 0 ? (
                                <Accordion key={index} className="event-schedule-accordion">
                                    <AccordionSummary expandIcon={<ExpandMore color="white" />}>
                                        <div className="event-schedule-summary">
                                            <div className="event-schedule-date">
                                                <Body2 color="var(--secondary-color)">
                                                    {formatEventDate(day?.date)}
                                                </Body2>
                                            </div>
                                            <div className="event-schedule-time">
                                                <Body3>
                                                    🕒 {day?.from_time} - {day?.to_time}
                                                </Body3>
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            paddingTop: "0",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "0.5rem",
                                        }}
                                    >
                                        {day.slots.map((slot, slotIndex) => (
                                            <div key={slotIndex} className="event-schedule-slot">
                                                <Body3>
                                                    Slot {slotIndex + 1}: {slot.from_time} - {slot.to_time}
                                                </Body3>
                                            </div>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            ) : (
                                <div
                                    key={index}
                                    className="event-schedule-accordion event-schedule-single"
                                >
                                    <div className="event-schedule-summary">
                                        <div className="event-schedule-date">
                                            <Body2 color="var(--secondary-color)">
                                                {formatEventDate(day?.date)}
                                            </Body2>
                                        </div>
                                        <div className="event-schedule-time">
                                            <Body3>
                                                🕒 {day?.from_time} - {day?.to_time}
                                            </Body3>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            <div className="event-schedule-footer">
                <Body3>
                    Page: {page} of {totalPages}
                </Body3>
                <div className="event-schedule-controls">
                    <IconButton
                        onClick={handlePrev}
                        disabled={page === 1 || totalPages === 1}
                        sx={{
                            "&.Mui-disabled": {
                                color: "var(--quaternary-color)",
                                opacity: "0.5",
                                background: "transparent",
                            },
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                    <IconButton
                        onClick={handleNext}
                        disabled={page === totalPages || totalPages === 1}
                        sx={{
                            "&.Mui-disabled": {
                                color: "var(--quaternary-color)",
                                opacity: "0.5",
                                background: "transparent",
                            },
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                </div>
            </div>
        </div>
    );
}